import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import instance from "@/lib/axios";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import type {
  MarkReadResponse,
  NotificationAudience,
} from "@/types/notification";

import { notificationKeys } from "./keys";

// A notification list query key ends with its FetchNotificationsParams object
// (see notificationKeys.list). Only `audience` matters for scoping the
// invalidation; anything off-contract degrades to "no audience" (= refresh),
// same as the unscoped case.
const listKeyParamsSchema = z
  .object({ audience: z.string().optional() })
  .catch({});

/**
 * Clear the caller's unread notifications.
 *
 * Pass the calling area's `audience` so clearing one inbox (say /supervisor)
 * cannot silently zero the badges of the areas the user isn't looking at.
 * Omitting it clears every area, which is only right for an unscoped bell.
 */
export function useMarkAllRead(audience?: NotificationAudience) {
  const orgSlug = useOrganizationStore((s) => s.orgSlug);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<MarkReadResponse> => {
      const response = await instance.post<MarkReadResponse>(
        `/api/${orgSlug}/notifications/read-all/`,
        audience ? { audience } : {},
      );
      return response.data;
    },
    onSuccess: () => {
      if (!audience) {
        queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
        queryClient.invalidateQueries({
          queryKey: notificationKeys.unreadCounts(),
        });
        return;
      }
      // Only this area's rows changed. An unscoped list/badge still counts them,
      // so refresh that too — but leave the other areas' caches alone.
      queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(),
        predicate: (query) => {
          const params = listKeyParamsSchema.parse(query.queryKey[2]);
          return !params.audience || params.audience === audience;
        },
      });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(audience),
      });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(),
      });
    },
    meta: { errorMessage: "Couldn't mark all as read." },
  });
}

export default useMarkAllRead;
